import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Card, CardContent,
  Autocomplete, Chip, Alert, Snackbar, IconButton, LinearProgress,
  Container, Breadcrumbs, Link, Divider, InputAdornment, Tooltip,
  CircularProgress, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions
} from '@mui/material';
import {
  Person, Business, Save, Search, CloudUpload, AttachFile,
  Delete as DeleteIcon, ArrowBack, Calculate, AddCircleOutline,
  Edit as EditIcon, Visibility as ViewIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import { getSellers, createSellerLedger, getSellerLedger, updateSellerLedger } from '../../services/purchase';
import { getProjects } from '../../services/project';
import { hasWritePermission, hasReadPermission } from '../../utils/menuFilter';
import { useHistory } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';
import { exportAsExcel, exportAsCSV, exportAsPDF } from '../../utils/importExport';
import ExportButton from '../../components/ExportButton/ExportButton';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';

const FarmerLedger = ({ onBack }) => {
  const history = useHistory();
  const canWriteFarmerLedger = hasWritePermission('purchase.farmer-ledger') || hasWritePermission('farmer-ledger');
  const canReadFarmerLedger = hasReadPermission('purchase.farmer-ledger') || hasReadPermission('farmer-ledger');
  const [filters, setFilters] = useState({
    sellerId: null,
    projectId: null,
    fromDate: '',
    toDate: ''
  });

  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [sellersPage, setSellersPage] = useState(1);
  const [hasMoreSellers, setHasMoreSellers] = useState(true);
  const [allSellersLoaded, setAllSellersLoaded] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [currentRecord, setCurrentRecord] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);

  const [formData, setFormData] = useState({
    sellerId: '',
    projectId: '',
    amount: '',
    transactionType: 'add',
    paymentMode: 'cash',
    description: '',
    transactionDate: '',
    referenceId: ''
  });

  const [formErrors, setFormErrors] = useState({});
  
  // Centralized Modal States
  const [modalState, setModalState] = useState({
    alert: { open: false, title: '', message: '' },
    confirm: { open: false, title: '', message: '', onConfirm: null, type: 'info' }
  });

  const showAlert = (message, title = 'Alert') => {
    setModalState(prev => ({ ...prev, alert: { open: true, title, message } }));
  };

  const showConfirm = (message, onConfirm, title = 'Confirm', type = 'info') => {
    setModalState(prev => ({ ...prev, confirm: { open: true, title, message, onConfirm, type } }));
  };

  useEffect(() => {
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await getProjects();
        setProjects(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error loading projects:', error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, []);

  const loadSellers = async (page = 1, append = false) => {
    if (page === 1) {
      setLoadingSellers(true);
    }

    try {
      const params = { page, limit: 20 };
      const response = await getSellers(params);

      const sellersData = response.data || [];
      const pagination = response.pagination || {};

      if (append) {
        setSellers(prev => [...prev, ...sellersData]);
      } else {
        setSellers(sellersData);
      }

      const totalPages = pagination.totalPages || 1;
      const currentPage = pagination.page || page;

      setHasMoreSellers(currentPage < totalPages);
      setAllSellersLoaded(!hasMoreSellers || currentPage >= totalPages);

    } catch (error) {
      console.error('Error loading sellers:', error);
      if (!append) {
        setSellers([]);
      }
    } finally {
      if (page === 1) {
        setLoadingSellers(false);
      }
    }
  };

  useEffect(() => {
    loadSellers(1, false);
  }, []);

  const loadLedgerData = async (pageNum = 0, rows = 10) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum + 1,
        limit: rows,
        ...(filters.sellerId && { sellerId: filters.sellerId._id }),
        ...(filters.projectId && { projectId: filters.projectId._id }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate })
      };

      const response = await getSellerLedger(params);

      const data = response.data || response;
      const ledgerDataArray = Array.isArray(data) ? data : (data.docs || data.data || []);

      const pagination = response.pagination || response.extra || {};
      const total = response.totalRecords || pagination.totalRecords || response.total || response.totalDocs || ledgerDataArray.length;

      setLedgerData(ledgerDataArray);
      setTotalRecords(total);
    } catch (error) {
      console.error('Error loading ledger data:', error);
      setLedgerData([]);
      setTotalRecords(0);
      showAlert('Error loading ledger data: ' + error.message, 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLedgerData();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetSellersPagination = () => {
    setSellersPage(1);
    setHasMoreSellers(true);
    setAllSellersLoaded(false);
  };

  const applyFilters = () => {
    setPage(0);
    loadLedgerData(0, rowsPerPage);
  };

  const clearFilters = () => {
    setFilters({
      sellerId: null,
      projectId: null,
      fromDate: '',
      toDate: ''
    });
    setPage(0);
    loadLedgerData(0, rowsPerPage);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadLedgerData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadLedgerData(0, newRowsPerPage);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.sellerId) errors.sellerId = 'Seller is required';
    if (!formData.projectId) errors.projectId = 'Project is required';
    if (!formData.amount || parseFloat(formData.amount) <= 0) errors.amount = 'Valid amount is required';
    if (!formData.transactionDate) errors.transactionDate = 'Transaction date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const payload = {
        sellerId: formData.sellerId,
        projectId: formData.projectId,
        amount: parseFloat(formData.amount),
        transactionType: formData.transactionType,
        paymentMode: formData.paymentMode.toLowerCase(),
        referenceId: formData.referenceId,
        description: formData.description,
        transactionDate: formData.transactionDate
      };

      if (dialogMode === 'add') {
        const response = await createSellerLedger(payload);

        await loadLedgerData(page, rowsPerPage);
        showAlert('Transaction added successfully', 'Success');
      } else {
        const response = await updateSellerLedger(currentRecord._id, payload);

        await loadLedgerData(page, rowsPerPage);
        showAlert('Transaction updated successfully', 'Success');
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving transaction:', error);
      showAlert('Error saving transaction: ' + error.message, 'Error');
    }
  };

  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setFormData({
      sellerId: '',
      projectId: '',
      amount: '',
      transactionType: 'add',
      paymentMode: 'cash',
      description: '',
      transactionDate: '',
      referenceId: ''
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (record) => {
    setDialogMode('edit');
    setCurrentRecord(record);
    setFormData({
      sellerId: record.sellerId?._id || '',
      projectId: record.projectId?._id || '',
      amount: record.amount,
      transactionType: record.transactionType,
      paymentMode: record.paymentMode?.toLowerCase(),
      description: record.description,
      transactionDate: record.transactionDate ? new Date(record.transactionDate).toISOString().split('T')[0] : '',
      referenceId: record.referenceId
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRecord(null);
  };

  const handleOpenViewDialog = (record) => {
    setCurrentRecord(record);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setCurrentRecord(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const buildExportRows = (list) => {
    return list.map((record) => ({
      'Seller Name': record.sellerId?.farmerName || 'N/A',
      'Project': record.projectId?.projectName || 'N/A',
      'Amount': record.amount,
      'Type': record.transactionType?.toUpperCase(),
      'Payment Mode': record.paymentMode ? record.paymentMode.charAt(0).toUpperCase() + record.paymentMode.slice(1) : 'N/A',
      'Description': record.description || '-',
      'Date': formatDate(record.transactionDate),
      'Reference ID': record.referenceId || '-'
    }));
  };

  const handleExportPdf = () => {
    if (!ledgerData.length) {
      showAlert('No ledger data available to export.');
      return;
    }
    const rows = buildExportRows(ledgerData);
    exportAsPDF({ rows, title: 'Farmer Ledger', fileNameBase: 'Farmer_Ledger_Export' });
  };

  const handleExportCsv = () => {
    if (!ledgerData.length) {
      showAlert('No ledger data available to export.');
      return;
    }
    const rows = buildExportRows(ledgerData);
    exportAsCSV({ rows, fileNameBase: 'Farmer_Ledger_Export' });
  };

  const handleExportExcel = () => {
    if (!ledgerData.length) {
      showAlert('No ledger data available to export.');
      return;
    }
    const rows = buildExportRows(ledgerData);
    exportAsExcel({ rows, sheetName: 'Farmer Ledger', fileNameBase: 'Farmer_Ledger_Export' });
  };

  if (!canReadFarmerLedger) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Farmer Ledger. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{ 
              backgroundColor: '#536DFE', 
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none' 
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Breadcrumbs sx={{ mb: 1 }}>
            <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={onBack}>Purchases</Link>
            <Typography color="text.primary">Farmer Ledger</Typography>
          </Breadcrumbs>
          <Typography variant="h4" fontWeight="bold" color="primary.main">Farmer Ledger Management</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadLedgerData(page, rowsPerPage)}
            sx={{ width: { xs: '100%', sm: 'auto' }, textTransform: 'none', fontWeight: 600 }}
          >
            Refresh
          </Button>
          {/* <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ width: { xs: '100%', sm: 'auto' }, textTransform: 'none', fontWeight: 600 }}>Back to List</Button> */}
        </Box>
      </Box>

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom><Search color="primary" sx={{ mr: 1 }} />Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={sellers}
                value={filters.sellerId}
                onChange={(e, v) => handleFilterChange('sellerId', v)}
                getOptionLabel={(option) => option.farmerName || ''}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                loading={loadingSellers}
                onOpen={() => {
                  resetSellersPagination();
                  if (sellers.length === 0 && !allSellersLoaded) {
                    loadSellers(1, false);
                  }
                }}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight * 0.8 &&
                      hasMoreSellers &&
                      !loadingSellers
                    ) {
                      loadSellers(sellersPage + 1, true);
                      setSellersPage(prev => prev + 1);
                    }
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Seller Name"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSellers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={projects}
                value={filters.projectId}
                onChange={(e, v) => handleFilterChange('projectId', v)}
                getOptionLabel={(option) => option.projectName || ''}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                loading={loadingProjects}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button variant="contained" onClick={applyFilters}>Search</Button>
                <Button variant="outlined" onClick={clearFilters}>Reset</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
        <Typography variant="h5" fontWeight="bold">Ledger Transactions ({totalRecords})</Typography>
        {canWriteFarmerLedger && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
              variant="outlined"
            />
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={handleOpenAddDialog}
              sx={{ flex: 1 }}
            >
              Add Transaction
            </Button>
          </Box>
        )}
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                      <TableCell><strong>Seller Name</strong></TableCell>
                      <TableCell><strong>Project</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Payment Mode</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Reference ID</strong></TableCell>
                      <TableCell><strong>{canWriteFarmerLedger ? 'Actions' : 'Status'}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledgerData.map((record) => (
                      <TableRow key={record._id} hover>
                        <TableCell>{record.sellerId?.farmerName || 'N/A'}</TableCell>
                        <TableCell>{record.projectId?.projectName || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatCurrency(record.amount)}
                            color={record.transactionType === 'add' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.transactionType.toUpperCase()}
                            color={record.transactionType === 'add' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{record.paymentMode ? record.paymentMode.charAt(0).toUpperCase() + record.paymentMode.slice(1) : 'N/A'}</TableCell>
                        <TableCell>{record.description || '-'}</TableCell>
                        <TableCell>{formatDate(record.transactionDate)}</TableCell>
                        <TableCell>{record.referenceId || '-'}</TableCell>
                        <TableCell>
                          {canWriteFarmerLedger && (
                            <IconButton size="small" onClick={() => handleOpenEditDialog(record)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={() => handleOpenViewDialog(record)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                         
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {ledgerData.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>No transactions found</Typography>
                </Box>
              )}

              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                
              />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {currentRecord && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Seller Name</Typography>
                <Typography variant="body1">{currentRecord.sellerId?.farmerName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Project</Typography>
                <Typography variant="body1">{currentRecord.projectId?.projectName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                <Typography variant="body1">{formatCurrency(currentRecord.amount)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Transaction Type</Typography>
                <Chip
                  label={currentRecord.transactionType?.toUpperCase()}
                  color={currentRecord.transactionType === 'add' ? 'success' : 'warning'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Payment Mode</Typography>
                <Typography variant="body1">{currentRecord.paymentMode ? currentRecord.paymentMode.charAt(0).toUpperCase() + currentRecord.paymentMode.slice(1) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Reference ID</Typography>
                <Typography variant="body1">{currentRecord.referenceId || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Transaction Date</Typography>
                <Typography variant="body1">{formatDate(currentRecord.transactionDate)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                <Typography variant="body1">{formatDate(currentRecord.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{currentRecord.description || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Transaction' : 'Edit Transaction'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={sellers}
                value={sellers.find(s => s._id === formData.sellerId) || null}
                onChange={(e, v) => handleFormChange('sellerId', v?._id || '')}
                getOptionLabel={(option) => option.farmerName || ''}
                onOpen={() => {
                  resetSellersPagination();
                  if (sellers.length === 0 && !allSellersLoaded) {
                    loadSellers(1, false);
                  }
                }}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight * 0.8 &&
                      hasMoreSellers &&
                      !loadingSellers
                    ) {
                      loadSellers(sellersPage + 1, true);
                      setSellersPage(prev => prev + 1);
                    }
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Seller *"
                    error={!!formErrors.sellerId}
                    helperText={formErrors.sellerId}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={projects}
                value={projects.find(p => p._id === formData.projectId) || null}
                onChange={(e, v) => handleFormChange('projectId', v?._id || '')}
                getOptionLabel={(option) => option.projectName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project *"
                    error={!!formErrors.projectId}
                    helperText={formErrors.projectId}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                value={formData.amount}
                onChange={(e) => handleFormChange('amount', e.target.value)}
                error={!!formErrors.amount}
                helperText={formErrors.amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Transaction Type *"
                value={formData.transactionType}
                onChange={(e) => handleFormChange('transactionType', e.target.value)}
              >
                <MenuItem value="add">Add</MenuItem>
                <MenuItem value="deduct">Deduct</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Payment Mode"
                value={formData.paymentMode}
                onChange={(e) => handleFormChange('paymentMode', e.target.value)}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference ID"
                value={formData.referenceId}
                onChange={(e) => handleFormChange('referenceId', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Transaction Date *"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.transactionDate}
                onChange={(e) => handleFormChange('transactionDate', e.target.value)}
                error={!!formErrors.transactionDate}
                helperText={formErrors.transactionDate}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} startIcon={<Save />}>
            {dialogMode === 'add' ? 'Add Transaction' : 'Update Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={modalState.alert.open}
        title={modalState.alert.title}
        message={modalState.alert.message}
        onClose={() => setModalState(prev => ({ ...prev, alert: { ...prev.alert, open: false } }))}
      />

      <ConfirmModal
        open={modalState.confirm.open}
        title={modalState.confirm.title}
        type={modalState.confirm.type}
        onClose={() => setModalState(prev => ({ ...prev, confirm: { ...prev.confirm, open: false } }))}
        onConfirm={modalState.confirm.onConfirm}
      >
        {modalState.confirm.message}
      </ConfirmModal>
    </Container>
  );
};

export default FarmerLedger;